import { hasAnyGoalTarget, RegionId, zeroTargets } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useFeature, useNow, useRegionId } from "store/selectors/hooks";
import { MetricProgress } from "../marketing-goals/metric-progress-calc";
import { currentYearMonth } from "../marketing-goals/pace";
import { useMetricProgress } from "../marketing-goals/use-metric-progress";
import { metricFallsShortOfGoal, PlanHealth } from "./plan-health";

const fallsShortOfGoal = (progress: MetricProgress | null): boolean =>
    progress ? metricFallsShortOfGoal(progress.target, progress.projected) : false;

const hasGoal = (progress: MetricProgress | null): boolean => (progress?.target ?? 0) > 0;

export const usePlanHealth = (): PlanHealth => {
    const regionId = useRegionId();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const trpcClient = trpc.useTRPC();
    const now = useNow();

    const attainmentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({
            month: currentYearMonth(now),
        }),
    });
    const playbookQuery = useQuery({
        ...trpcClient.marketingPlaybook.myPlaybook.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });
    const visitProgress = useMetricProgress("visit");
    const otherTouchpointsProgress = useMetricProgress("otherTouchpoints");

    const isLoading =
        attainmentQuery.isLoading ||
        playbookQuery.isLoading ||
        visitProgress.isLoading ||
        otherTouchpointsProgress.isLoading;
    const hasNoTouchpoints = (playbookQuery.data?.touchpoints.length ?? 0) === 0;

    const hasAnyGoal = hasAnyGoalTarget(attainmentQuery.data?.goal ?? zeroTargets());
    const hasTouchpointGoal =
        hasGoal(visitProgress.progress) || hasGoal(otherTouchpointsProgress.progress);
    const isFallingShortOfGoal =
        fallsShortOfGoal(visitProgress.progress) ||
        fallsShortOfGoal(otherTouchpointsProgress.progress);

    return {
        isLoading,
        isPlaybookEnabled: marketingPlaybookEnabled,
        hasRegion: !!regionId,
        hasNoTouchpoints,
        hasAnyGoal,
        hasTouchpointGoal,
        isFallingShortOfGoal,
    };
};
